import { Signal } from 'typed-signals';
import type {
  OfferDefinition,
  ConditionHandler,
  OfferSystemConfig,
  BaseCondition,
  ConditionGroup,
  OfferConditionsReport,
} from './offerSystemTypes';
import type { PurchasesList } from './OfferSystemHistory';
import { OfferSystemHistory } from './OfferSystemHistory';
import { MS_IN_MINUTE } from '../../utils/time-utils';

export interface ActiveOfferData<C extends BaseCondition, GD> {
  offer: OfferDefinition<C, GD>;
  variant: number;
  endDate: number | null;
  startDate: number;
  activationId: string;
}

export class OfferSystemController<S, C extends BaseCondition, GD> {
  public readonly onOfferActivated = new Signal<(offer: ActiveOfferData<C, GD>) => void>();

  public readonly onOfferDeactivated = new Signal<(offer: OfferDefinition<C, GD>) => void>();

  public readonly onOffersUpdated = new Signal<(offers: ActiveOfferData<C, GD>[]) => void>();

  private readonly history: OfferSystemHistory<S, C, GD>;

  public debugLastUpdateReport: Record<string, OfferConditionsReport<C>> = {};

  constructor(private readonly config: OfferSystemConfig<S, C, GD>) {
    this.history = new OfferSystemHistory(config);

    setInterval(() => {
      this.tryDeactivateOffers(null);
    }, 5000);
  }

  public getActiveOffer(offerId: string): ActiveOfferData<C, GD> | null {
    const offer = this.config.offers.find(({ id }) => id === offerId);
    if (!offer) return null;

    if (!this.history.getCurrentActivationId(offerId)) {
      const conditionsMet = offer.startConditions
        ? this.testConditionGroup(offer, null, offer.startConditions, null)
        : true;
      const variant = this.getVariantIndexToActivate(offer, null);
      if (conditionsMet && variant !== null) this.activateOffer(offer, variant);
    }

    return this.offerToActiveOfferData(offer);
  }

  public getActiveOffers(
    location: string,
    trigger: string | null = null,
    skipUpdate = false,
  ): ActiveOfferData<C, GD>[] {
    if (!skipUpdate) {
      this.updateOffers(location, trigger);
    }

    const allActiveOffers = this.getActiveOffersWithoutUpdate();

    if (!skipUpdate) {
      this.onOffersUpdated.emit(allActiveOffers);
    }

    return allActiveOffers.filter(({ offer }) => (location === '*' ? true : offer.location.includes(location)));
  }

  private getActiveOffersWithoutUpdate(): ActiveOfferData<C, GD>[] {
    return this.history
      .getActiveOffers()
      .sort((a, b) => b.offer.priority - a.offer.priority)
      .map((activeOfferData) => ({
        ...activeOfferData,
        endDate: this.getActivationEndDate(activeOfferData.offer),
      }));
  }

  public markPurchase(offerId: string): void {
    this.history.onPurchased(offerId);
    this.tryDeactivateOffers(null);
  }

  public getPurchasesLists(): PurchasesList {
    return this.history.getAllPurchases();
  }

  private tryDeactivateOffers(trigger: string | null): void {
    const { history } = this;

    let deactivatedOffer = false;

    history.getActiveOffers().forEach(({ offer, activationId }) => {
      const conditionsMet =
        offer.endConditions !== null
          ? this.testConditionGroup(offer, activationId, offer.endConditions, trigger)
          : false;
      const inTimeLimit = this.testIfInTimeLimit(offer);

      if (!conditionsMet && inTimeLimit) return;
      history.deactivateOffer(offer.id, this.getActivationEndDate(offer) ?? this.config.getCurrentTime());
      this.onOfferDeactivated.emit(offer);
      deactivatedOffer = true;
    });

    if (deactivatedOffer) {
      this.onOffersUpdated.emit(this.getActiveOffersWithoutUpdate());
    }
  }

  private updateOffers(location: string, trigger: string | null): void {
    this.debugLastUpdateReport = {};

    const { history } = this;

    this.tryDeactivateOffers(trigger);

    const activeOfferIds = history.getActiveOffers().map(({ offer }) => offer.id);
    const offersToActivate = this.config.offers
      .filter(({ id }) => !activeOfferIds.includes(id))
      .sort((a, b) => b.priority - a.priority)
      .filter((offer) => (location === '*' ? true : offer.location.includes(location)));

    offersToActivate.forEach((offer) => {
      const conditionsMet = offer.startConditions
        ? this.testConditionGroup(offer, null, offer.startConditions, trigger)
        : true;
      const variant = this.getVariantIndexToActivate(offer, trigger);
      if (conditionsMet && variant !== null) this.activateOffer(offer, variant);
    });
  }

  private activateOffer(offer: OfferDefinition<C, GD>, variant: number): void {
    this.history.activateOffer(offer.id, variant);

    const activeOffer = this.offerToActiveOfferData(offer);
    if (!activeOffer) return;
    this.onOfferActivated.emit(activeOffer);
  }

  private offerToActiveOfferData(offer: OfferDefinition<C, GD>): ActiveOfferData<C, GD> | null {
    const activationData = this.history.getCurrentActivationData(offer.id).current;
    if (!activationData) return null;

    return {
      offer,
      variant: activationData.variant,
      activationId: activationData.activationId,
      startDate: activationData.startDate,
      endDate: this.getActivationEndDate(offer),
    };
  }

  private getVariantIndexToActivate(offer: OfferDefinition<C, GD>, trigger: string | null): number | null {
    const index = offer.variants.findIndex((variant) =>
      variant.conditions ? this.testConditionGroup(offer, null, variant.conditions, trigger) : true,
    );
    return index === -1 ? null : index;
  }

  // private testConditionsGroups(
  //   offer: OfferDefinition<C, GD>,
  //   activationId: string | null,
  //   conditionsGroups: ConditionGroup<C>[],
  //   trigger: string | null,
  // ): boolean {
  //   const result = conditionsGroups.map((group) => this.testConditionGroup(offer, activationId, group, trigger));
  //   return result.every((v) => v);
  // }

  private testConditionGroup(
    offer: OfferDefinition<C, GD>,
    activationId: string | null,
    conditionGroup: ConditionGroup<C>,
    trigger: string | null,
  ): boolean {
    const results = conditionGroup.conditions.map((condition) =>
      this.invokeConditionHandler(condition, offer, activationId, trigger),
    );

    this.addOfferConditionsToDebugReport(offer, conditionGroup, results);
    return conditionGroup.groupType === 'AND' ? results.every((v) => v) : results.some((v) => v);
  }

  private addOfferConditionsToDebugReport(
    offer: OfferDefinition<C, GD>,
    conditionGroup: ConditionGroup<C>,
    results: boolean[],
  ): void {
    if (!this.debugLastUpdateReport[offer.id]) {
      this.debugLastUpdateReport[offer.id] = new Map();
    }

    this.debugLastUpdateReport[offer.id].set(conditionGroup, results);
  }

  private testIfInTimeLimit(offer: OfferDefinition<C, GD>): boolean {
    const {
      config: { getCurrentTime },
      history,
    } = this;
    const {
      timeLimit: { durationMinutes, endDate },
    } = offer;

    if (durationMinutes) {
      const activationTime = history.getActivationTime(offer.id);
      if (activationTime === null) return false;
      const passedMinutes = Math.floor((getCurrentTime() - activationTime) / MS_IN_MINUTE);
      if (passedMinutes >= durationMinutes) return false;
    }

    if (endDate && endDate <= getCurrentTime()) return false;
    return true;
  }

  private getActivationEndDate(offer: OfferDefinition<C, GD>): number | null {
    const { endDate, durationMinutes } = offer.timeLimit;
    if (!endDate && !durationMinutes) return null;

    const activationTime = this.history.getActivationTime(offer.id);
    if (activationTime === null) return null;

    const minutesEndTime = activationTime + (durationMinutes ?? Infinity) * MS_IN_MINUTE;
    const calendarEndTime = endDate ?? Infinity;

    return Math.min(minutesEndTime, calendarEndTime);
  }

  private invokeConditionHandler(
    condition: ConditionGroup<C> | C,
    offer: OfferDefinition<C, GD>,
    activationId: string | null,
    trigger: string | null = null,
  ): boolean {
    if ('groupType' in condition) {
      return this.testConditionGroup(offer, activationId, condition, trigger);
    }

    const { offers, state } = this.config;
    const handler = this.config.conditions[condition.type as keyof typeof this.config.conditions] as ConditionHandler<
      S,
      C,
      GD
    >;
    return handler.testCondition({
      offer,
      allOffers: offers,
      condition,
      trigger,
      purchaseHistory: this.history,
      activationId,
      state,
    });
  }
}
